import React, { useMemo } from 'react';
import { ChartBar } from '@phosphor-icons/react';
import {
    ResponsiveContainer,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Tooltip,
} from 'recharts';
import { getSentimentColor, getChartGridColor } from '@t-one-internal/design-system';

interface CompetencyRow {
    attributeName: string;
    strengthCount: number;
    challengeCount: number;
    notEvaluatedCount: number;
    totalRatings: number;
    strengthPct: number;
}

interface CoachingCompetencyOverviewWidgetProps {
    data?: CompetencyRow[] | null;
    loading?: boolean;
    error?: string;
}

export const CoachingCompetencyOverviewWidget: React.FC<CoachingCompetencyOverviewWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    const totalStrength = rows.reduce((s, r) => s + (Number(r.strengthCount) || 0), 0);
    const totalChallenge = rows.reduce((s, r) => s + (Number(r.challengeCount) || 0), 0);
    const totalAll = totalStrength + totalChallenge + rows.reduce((s, r) => s + (Number(r.notEvaluatedCount) || 0), 0);

    // Top attributes by total ratings for radar (max 10 spokes)
    const radarData = useMemo(() => {
        const sorted = [...rows]
            .sort((a, b) => (Number(b.totalRatings) || 0) - (Number(a.totalRatings) || 0))
            .slice(0, 10);

        return sorted.map(r => {
            const total = Number(r.totalRatings) || 1;
            const name = (r.attributeName || '').length > 14
                ? r.attributeName.slice(0, 12) + '...'
                : r.attributeName;
            return {
                attribute: name,
                fullName: r.attributeName,
                strength: Math.round((Number(r.strengthCount) / total) * 100),
                challenge: Math.round((Number(r.challengeCount) / total) * 100),
            };
        });
    }, [rows]);

    // Top strengths & challenges for the list
    const topStrengths = useMemo(() =>
        [...rows]
            .filter(r => Number(r.strengthCount) > 0)
            .sort((a, b) => {
                const aP = Number(a.totalRatings) > 0 ? Number(a.strengthCount) / Number(a.totalRatings) : 0;
                const bP = Number(b.totalRatings) > 0 ? Number(b.strengthCount) / Number(b.totalRatings) : 0;
                return bP - aP;
            })
            .slice(0, 5),
        [rows]
    );

    const topChallenges = useMemo(() =>
        [...rows]
            .filter(r => Number(r.challengeCount) > 0)
            .sort((a, b) => {
                const aP = Number(a.totalRatings) > 0 ? Number(a.challengeCount) / Number(a.totalRatings) : 0;
                const bP = Number(b.totalRatings) > 0 ? Number(b.challengeCount) / Number(b.totalRatings) : 0;
                return bP - aP;
            })
            .slice(0, 5),
        [rows]
    );

    return (
        <div className="flex flex-col h-full p-5">
            <div className="flex-1 flex flex-col">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <ChartBar className="w-8 h-8" />
                        <span className="text-sm">No assessment data</span>
                    </div>
                ) : (
                    <>
                        {/* Summary pills */}
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-[10px] text-gray-400">{rows.length} competencies &middot; {totalAll} ratings</span>
                            <div className="flex-1" />
                            <div className="flex items-center gap-1.5 text-[10px]">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-gray-500">Strength</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px]">
                                <span className="w-2 h-2 rounded-full bg-rose-400" />
                                <span className="text-gray-500">Challenge</span>
                            </div>
                        </div>

                        {/* Radar chart */}
                        <div style={{ height: 220 }} className="mb-3">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                                    <PolarGrid stroke={getChartGridColor('grid')} />
                                    <PolarAngleAxis
                                        dataKey="attribute"
                                        tick={{ fontSize: 10, fill: getChartGridColor('axisLabel') }}
                                    />
                                    <PolarRadiusAxis
                                        angle={90}
                                        domain={[0, 100]}
                                        tick={{ fontSize: 9, fill: getChartGridColor('tickLabel') }}
                                        tickCount={3}
                                    />
                                    <Radar
                                        name="Strength %"
                                        dataKey="strength"
                                        stroke={getSentimentColor('positive')}
                                        fill={getSentimentColor('positive')}
                                        fillOpacity={0.25}
                                        strokeWidth={2}
                                    />
                                    <Radar
                                        name="Challenge %"
                                        dataKey="challenge"
                                        stroke={getSentimentColor('negative')}
                                        fill={getSentimentColor('negative')}
                                        fillOpacity={0.15}
                                        strokeWidth={2}
                                    />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0].payload;
                                            return (
                                                <div style={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #E5E7EB',
                                                    borderRadius: '8px',
                                                    padding: '8px 12px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                }}>
                                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                                                        {d.fullName}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: getSentimentColor('positive') }}>Strength: {d.strength}%</div>
                                                    <div style={{ fontSize: '11px', color: getSentimentColor('negative') }}>Challenge: {d.challenge}%</div>
                                                </div>
                                            );
                                        }}
                                    />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Bottom: two columns — top strengths & top challenges */}
                        <div className="grid grid-cols-2 gap-4 flex-1 overflow-auto">
                            {/* Strengths */}
                            <div>
                                <div className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide mb-2">Top Strengths</div>
                                <div className="space-y-1.5">
                                    {topStrengths.map((r, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold flex items-center justify-center shrink-0">
                                                {i + 1}
                                            </span>
                                            <span className="text-[11px] text-gray-700 truncate">{r.attributeName}</span>
                                        </div>
                                    ))}
                                    {topStrengths.length === 0 && <span className="text-[11px] text-gray-400">None identified</span>}
                                </div>
                            </div>

                            {/* Challenges */}
                            <div>
                                <div className="text-[10px] font-semibold text-rose-600 uppercase tracking-wide mb-2">Top Challenges</div>
                                <div className="space-y-1.5">
                                    {topChallenges.map((r, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-700 text-[9px] font-bold flex items-center justify-center shrink-0">
                                                {i + 1}
                                            </span>
                                            <span className="text-[11px] text-gray-700 truncate">{r.attributeName}</span>
                                        </div>
                                    ))}
                                    {topChallenges.length === 0 && <span className="text-[11px] text-gray-400">None identified</span>}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
